/**
 * Test data — every record a run created is KEPT until a designer cleans it.
 * Cleaning deletes through the application's own engine (business logic and
 * cascades apply), newest first, and skips any record changed since its test.
 */

import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Eraser } from 'lucide-react'
import Button from '@/components/common/Button'
import { useConfirm } from '@/components/common/ConfirmDialog'
import {
  apiError, cleanTestData, getTestData, type CleanResult, type LedgerEntry, type LedgerSummary,
} from '@/api/uiTesting'
import { Section, fmtTime, mutedText, panel, tableStyle, tdStyle, thStyle } from './shared'
import { nice } from './plainSteps'

interface Props {
  project: string
  canTest: boolean
  onChanged: () => Promise<void> | void
}

const EXPLAIN = 'Records are deleted through the application’s own engine — its business logic and '
  + 'cascades apply, exactly as for a person’s delete — newest first. A record changed since its test '
  + 'finished is left alone and listed, never deleted.'

export default function TestDataTab({ project, canTest, onChanged }: Props) {
  const [summary, setSummary] = useState<LedgerSummary | null>(null)
  const [records, setRecords] = useState<LedgerEntry[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [result, setResult] = useState<{ scope: string; r: CleanResult } | null>(null)
  const { confirm, confirmDialog } = useConfirm()

  const load = useCallback(async () => {
    try {
      const r = await getTestData(project)
      setSummary(r.summary)
      setRecords(r.records)
    } catch (err) {
      toast.error(apiError(err, 'Could not load the test-data ledger'))
    }
  }, [project])

  useEffect(() => { void load() }, [load])

  const clean = async (runId: string | null) => {
    const scope = runId ? `run ${runId}` : 'every run'
    const n = runId ? summary?.runs[runId]?.kept ?? 0 : summary?.kept ?? 0
    if (!(await confirm({
      title: runId ? 'Clean this run’s test data' : 'Clean all test data',
      message: <>Delete the {n} record(s) kept by {scope}?<br /><br />{EXPLAIN}</>,
      confirmLabel: 'Clean',
    }))) return
    setBusy(runId || '*')
    try {
      const r = await cleanTestData(project, runId)
      setResult({ scope, r })
      toast.success(`Deleted ${r.deleted}; ${r.already_gone} already gone; `
        + `${r.changed_since_test.length} changed since the test; ${r.failed.length} refused`)
      await load()
      await onChanged()
    } catch (err) {
      toast.error(apiError(err, 'Could not clean the test data'))
    } finally {
      setBusy(null)
    }
  }

  const runs = Object.entries(summary?.runs || {}).sort((a, b) => b[0].localeCompare(a[0]))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <section style={{ ...panel, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
          {summary?.kept ? `${summary.kept} test record${summary.kept === 1 ? ' is' : 's are'} being kept` : 'No test records are being kept'}
        </h2>
        <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 15, lineHeight: 1.55, maxWidth: 820 }}>
          Records a test run creates are kept in the preview database so you can look at what it left behind. {EXPLAIN}
        </p>
      </section>
      <div style={{ ...panel, padding: '20px 24px' }}>

      <Section title="By run" actions={canTest && (
        <Button size="sm" variant="danger" icon={<Eraser size={13} />} loading={busy === '*'}
                disabled={!summary?.kept} onClick={() => void clean(null)}>
          Clean all test data
        </Button>
      )}>
        {runs.length === 0 ? <div style={mutedText}>No run has created records.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>Run</th><th style={thStyle}>Kept</th><th style={thStyle}>Cleaned</th>
                <th style={thStyle}>Already gone</th><th style={thStyle} />
              </tr></thead>
              <tbody>
                {runs.map(([id, c]) => (
                  <tr key={id}>
                    <td style={tdStyle}><code>{id}</code></td>
                    <td style={tdStyle}>{c.kept}</td>
                    <td style={tdStyle}>{c.cleaned}</td>
                    <td style={tdStyle}>{c.gone}</td>
                    <td style={tdStyle}>
                      {canTest && c.kept > 0 && (
                        <Button size="sm" variant="secondary" loading={busy === id} onClick={() => void clean(id)}>
                          Clean up
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      </div>

      {result && (
        <Section title={`Last clean-up (${result.scope})`}>
          <div style={{ fontSize: 14 }}>
            {result.r.deleted} deleted · {result.r.already_gone} already gone ·
            {' '}{result.r.changed_since_test.length} changed since the test · {result.r.failed.length} refused
          </div>
          {result.r.changed_since_test.length > 0 && (
            <ul style={{ ...mutedText, margin: '6px 0', paddingLeft: 18 }}>
              {result.r.changed_since_test.map((x, i) => (
                <li key={i}>{x.obj} {x.txn_id} (case {x.case}) — changed since its test, left in place</li>
              ))}
            </ul>
          )}
          {result.r.failed.length > 0 && (
            <ul style={{ margin: '6px 0', paddingLeft: 18, fontSize: 13, color: 'var(--color-error, #dc2626)' }}>
              {result.r.failed.map((x, i) => <li key={i}>{x.obj} {x.txn_id}: {x.error}</li>)}
            </ul>
          )}
        </Section>
      )}

      <div style={{ ...panel, padding: '20px 24px' }}>
      <Section title="Kept records">
        {records.length === 0 ? <div style={mutedText}>None.</div> : (
          <div style={{ overflowX: 'auto', maxHeight: '50vh', overflowY: 'auto' }}>
            <table style={tableStyle}>
              <thead><tr>
                {['Object', 'Record', 'Run', 'Case', 'Created by', 'Created'].map((h) => <th key={h} style={thStyle}>{h}</th>)}
              </tr></thead>
              <tbody>
                {records.slice().reverse().map((r) => (
                  <tr key={`${r.obj}/${r.txn_id}`}>
                    <td style={tdStyle}>{nice(r.obj, true)}</td>
                    <td style={tdStyle}><code>{r.txn_id}</code></td>
                    <td style={tdStyle}><code>{r.run_id}</code></td>
                    <td style={tdStyle}><code>{r.case}</code></td>
                    <td style={tdStyle}>{r.via === 'ui' ? 'the browser' : r.via === 'unit' ? 'a unit test' : 'a precondition'}</td>
                    <td style={tdStyle}>{fmtTime(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      </div>
      {confirmDialog}
    </div>
  )
}
